import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Presentation, ArrowRight } from 'lucide-react';
interface FoundingGroupsSlideProps {
  isDark?: boolean;
}
export function FoundingGroupsSlide({
  isDark = false
}: FoundingGroupsSlideProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  type WaveType = 'closed' | 'active' | 'milestone' | 'future';
  const waves: {
    name: string;
    date: string;
    status: string | null;
    description: string | null;
    type: WaveType;
    icon?: typeof Calendar | typeof Presentation;
  }[] = [{
    name: 'Launch Wave',
    date: 'February 18, 2026',
    status: 'Closed',
    description: 'Internal and early validation group focused on platform readiness and leadership insight.',
    type: 'closed'
  }, {
    name: 'Founding Group · Wave 1',
    date: 'April 15, 2026',
    status: null,
    description: 'First external cohort shaping Heliona through real-world leadership use and feedback.',
    type: 'active'
  }, {
    name: 'Founding Group · Wave 2',
    date: 'June 17, 2026',
    status: null,
    description: 'Expanded access with refined tools, deeper insights, and growing organizational impact.',
    type: 'active'
  }, {
    name: 'Founding Group · Wave 3',
    date: 'August 19, 2026',
    status: null,
    description: 'Continued expansion with validated outcomes from earlier cohorts.',
    type: 'active'
  }, {
    name: '4C Index · Public Presentation',
    date: 'October 15, 2026 · In-Person',
    status: null,
    description: 'Public presentation of our psychometric assessment results and the 4C Index.',
    type: 'milestone',
    icon: Presentation
  }, {
    name: 'Founding Group · Wave 4',
    date: 'October 22, 2026',
    status: null,
    description: 'Final founding cohort ahead of broader availability.',
    type: 'active'
  }];
  function getStyles(type: WaveType) {
    switch (type) {
      case 'closed':
        return {
          card: isDark ? 'bg-white/[0.02] border border-white/5 opacity-60' : 'bg-white border border-heliona-beige opacity-60',
          icon: isDark ? 'bg-white/5' : 'bg-heliona-beige',
          iconColor: isDark ? 'text-white/30' : 'text-heliona-text-gray/50',
          title: isDark ? 'text-white/50' : 'text-heliona-text-gray/50',
          date: isDark ? 'text-white/50' : 'text-heliona-text-gray/50',
          desc: isDark ? 'text-white/50' : 'text-heliona-text-gray/50'
        };
      case 'active':
        return {
          card: isDark ? 'bg-white/[0.03] border border-heliona-mint/40' : 'bg-white border border-heliona-mint/40 shadow-sm',
          icon: 'bg-heliona-mint/10 border border-heliona-mint/30',
          iconColor: isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark',
          title: isDark ? 'text-white' : 'text-heliona-charcoal',
          date: isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark',
          desc: isDark ? 'text-white/80' : 'text-heliona-text-gray'
        };
      case 'milestone':
        return {
          card: isDark ? 'bg-heliona-orange/5 border border-heliona-orange/30' : 'bg-heliona-orange/5 border border-heliona-orange/30 shadow-sm',
          icon: 'bg-heliona-orange/10 border border-heliona-orange/30',
          iconColor: 'text-heliona-orange',
          title: isDark ? 'text-white' : 'text-heliona-charcoal',
          date: 'text-heliona-orange',
          desc: isDark ? 'text-white/80' : 'text-heliona-text-gray'
        };
      case 'future':
        return {
          card: isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige',
          icon: 'bg-heliona-orange/10 border border-heliona-orange/30',
          iconColor: 'text-heliona-orange',
          title: isDark ? 'text-white' : 'text-heliona-charcoal',
          date: 'text-heliona-orange',
          desc: isDark ? 'text-white/80' : 'text-heliona-text-gray'
        };
    }
  }
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-414">
      <div className="min-h-full flex flex-col justify-start md:justify-center max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-20" data-id="element-415">
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-8 sm:mb-12" data-id="element-416">
          <h1 className={`text-2xl sm:text-4xl font-serif font-medium mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-417">
            Heliona Founding Groups
          </h1>
          <p className={`text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed px-4 ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-418">
            2026–2027 Pilot Program
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div variants={itemVariants} className="mb-8 sm:mb-12" data-id="element-419">
          <p className={`text-sm sm:text-base leading-relaxed text-center max-w-3xl mx-auto px-4 ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-420">
            Heliona is onboarding organizations through a limited Founding Group
            model to ensure a high-touch experience and thoughtful platform
            development before public launch. As part of the pilot review,
            organizations may reserve seats in a specific wave. At the end of
            each wave, companies have the option to fully onboard the rest of
            their leaders and become a Heliona customer.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="space-y-4 sm:space-y-5 mb-8 sm:mb-12" data-id="element-421">
          {waves.map((wave, index) => {
          const styles = getStyles(wave.type);
          const IconComponent = wave.icon || Calendar;
          return <motion.div key={index} variants={itemVariants} className={`relative ${index < waves.length - 1 ? 'pb-4 sm:pb-5' : ''}`} data-id="element-422">
                <div className={`rounded-xl p-4 sm:p-6 transition-all ${styles.card}`} data-id="element-423">
                  <div className="flex items-start gap-3 sm:gap-4" data-id="element-424">
                    {/* Icon */}
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${styles.icon}`} data-id="element-425">
                      <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${styles.iconColor}`} data-id="element-426" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-427">
                      <div className="flex items-start justify-between gap-2 mb-2" data-id="element-428">
                        <h3 className={`text-base sm:text-lg font-bold leading-tight ${styles.title}`} data-id="element-429">
                          {wave.name}
                        </h3>
                        {wave.status && <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium flex-shrink-0 ${isDark ? 'bg-white/5 border border-white/10 text-white/50' : 'bg-heliona-beige border border-heliona-beige text-heliona-text-gray/70'}`} data-id="element-430">
                            {wave.status}
                          </span>}
                      </div>
                      <p className={`text-xs sm:text-sm font-medium mb-2 ${styles.date}`} data-id="element-431">
                        {wave.date}
                      </p>
                      {wave.description && <p className={`text-xs sm:text-sm leading-relaxed ${styles.desc}`} data-id="element-432">
                          {wave.description}
                        </p>}
                    </div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < waves.length - 1 && <div className={`absolute left-[1.75rem] sm:left-[2.25rem] top-[4.5rem] sm:top-[5.5rem] w-0.5 h-4 sm:h-5 bg-gradient-to-b to-transparent ${isDark ? 'from-white/10' : 'from-heliona-beige'}`} data-id="element-433" />}
              </motion.div>;
        })}
        </div>

        {/* 2027 Outlook */}
        <motion.div variants={itemVariants} className={`rounded-xl p-4 sm:p-6 mb-8 sm:mb-12 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-heliona-beige/30 border border-heliona-beige'}`} data-id="element-434">
          <div className="flex items-start gap-3 sm:gap-4" data-id="element-435">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/5' : 'bg-white border border-heliona-beige'}`} data-id="element-436">
              <ArrowRight className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? 'text-white/60' : 'text-heliona-charcoal/60'}`} data-id="element-437" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-438">
              <h3 className={`text-base sm:text-lg font-bold leading-tight mb-2 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-439">
                Looking Ahead · 2027
              </h3>
              <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-440">
                The Pilot Program continues into 2027 with additional cohorts.
                Public launch is planned for{' '}
                <span className="font-semibold" data-id="element-441">October 2027</span>.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div variants={itemVariants} className="text-center mb-16 sm:mb-0" data-id="element-442">
          <p className={`text-xs sm:text-sm italic leading-relaxed px-4 ${isDark ? 'text-white/60' : 'text-heliona-text-gray/70'}`} data-id="element-443">
            Limited waves. Thoughtful rollout. Built with leaders, not after
            them.
          </p>
        </motion.div>
      </div>
    </motion.div>;
}